export const SUPPORT_TABLE_COLUMNS = [
    { id: 'tasOccurrenceNumber', labelId: 'intl-msg:shared.occurrence.tas.number' },
    { id: 'supportType', labelId: 'intl-msg:tas.support.toolbar.supportType' },
    { id: 'status', labelId: 'intl-msg:shared.occurrence.tas.status' },
    { id: 'licensePlate', labelId: 'intl-msg:tas.occurrence.licensePlate' },
    { id: 'chassis', labelId: 'intl-msg:support.constants.field.chassis' },
    { id: 'dnDescription', labelId: 'intl-msg:tas.support.toolbar.dn' },
    { id: 'subject', labelId: 'intl-msg:support.boardItem.subjectLabel' },
    { id: 'subtopic', labelId: 'intl-msg:support.constants.field.subtopic' },
    { id: 'responsible', labelId: 'intl-msg:support.boardItem.ownerLabel' },
    { id: 'assignedTo', labelId: 'intl-msg:support.constants.field.assignedTo' },
    { id: 'statusUpdatedAt', labelId: 'intl-msg:tas.support.toolbar.sort.stepTime' },
    { id: 'updatedAt', labelId: 'intl-msg:tas.occurrence.updatedAt' },
] as const;

export type SupportTableColumn = (typeof SUPPORT_TABLE_COLUMNS)[number]['id'];

export const DEFAULT_SUPPORT_TABLE_COLUMN_ORDER: SupportTableColumn[] = [
    'tasOccurrenceNumber',
    'supportType',
    'status',
    'licensePlate',
    'chassis',
    'dnDescription',
    'subject',
    'subtopic',
    'responsible',
    'assignedTo',
    'statusUpdatedAt',
    'updatedAt',
];

export const isDefaultSupportTableColumnsSettings = (
    columnOrder: readonly SupportTableColumn[],
    hiddenColumns: readonly SupportTableColumn[]
) =>
    hiddenColumns.length === 0 &&
    columnOrder.length === DEFAULT_SUPPORT_TABLE_COLUMN_ORDER.length &&
    columnOrder.every((column, index) => column === DEFAULT_SUPPORT_TABLE_COLUMN_ORDER[index]);

export const DISABLED_SUPPORT_TABLE_COLUMNS: SupportTableColumn[] = ['tasOccurrenceNumber'];

const SUPPORT_TABLE_COLUMNS_STORAGE_KEY = 'support-table-columns';
const SUPPORT_TABLE_COLUMN_IDS = new Set<string>(DEFAULT_SUPPORT_TABLE_COLUMN_ORDER);
const LEGACY_SUPPORT_TABLE_COLUMN_IDS: Record<string, SupportTableColumn> = {
    number: 'tasOccurrenceNumber',
    dn: 'dnDescription',
    stepTime: 'statusUpdatedAt',
};

export interface SupportTableColumnsSettings {
    columnOrder: SupportTableColumn[];
    hiddenColumns: SupportTableColumn[];
}

const toSupportTableColumn = (value: unknown): SupportTableColumn | null => {
    if (typeof value !== 'string') {
        return null;
    }

    const columnId = LEGACY_SUPPORT_TABLE_COLUMN_IDS[value] ?? value;
    return SUPPORT_TABLE_COLUMN_IDS.has(columnId) ? (columnId as SupportTableColumn) : null;
};

const defaultSupportTableColumnsSettings = (): SupportTableColumnsSettings => ({
    columnOrder: [...DEFAULT_SUPPORT_TABLE_COLUMN_ORDER],
    hiddenColumns: [],
});

export const normalizeSupportTableColumnsSettings = (
    columnOrder: unknown,
    hiddenColumns: unknown
): SupportTableColumnsSettings => {
    const storedOrder = Array.isArray(columnOrder)
        ? columnOrder.reduce<SupportTableColumn[]>((columns, value) => {
              const column = toSupportTableColumn(value);
              if (column && !columns.includes(column)) {
                  columns.push(column);
              }
              return columns;
          }, [])
        : [];

    return {
        columnOrder: [
            ...storedOrder,
            ...DEFAULT_SUPPORT_TABLE_COLUMN_ORDER.filter(column => !storedOrder.includes(column)),
        ],
        hiddenColumns: Array.isArray(hiddenColumns)
            ? hiddenColumns.reduce<SupportTableColumn[]>((columns, value) => {
                  const column = toSupportTableColumn(value);
                  if (column && !DISABLED_SUPPORT_TABLE_COLUMNS.includes(column) && !columns.includes(column)) {
                      columns.push(column);
                  }
                  return columns;
              }, [])
            : [],
    };
};

const parseSupportTableColumnsSettings = (storedValue: string | null): SupportTableColumnsSettings | null => {
    if (!storedValue) {
        return null;
    }

    const parsed = JSON.parse(storedValue) as Partial<SupportTableColumnsSettings>;
    return normalizeSupportTableColumnsSettings(parsed.columnOrder, parsed.hiddenColumns);
};

export const readSupportTableColumnsSettings = (): SupportTableColumnsSettings => {
    try {
        const storedValue = localStorage.getItem(SUPPORT_TABLE_COLUMNS_STORAGE_KEY);
        const settings = parseSupportTableColumnsSettings(storedValue);
        if (settings) {
            return settings;
        }

        const sessionValue = sessionStorage.getItem(SUPPORT_TABLE_COLUMNS_STORAGE_KEY);
        const sessionSettings = parseSupportTableColumnsSettings(sessionValue);
        if (!sessionValue || !sessionSettings) {
            return defaultSupportTableColumnsSettings();
        }

        localStorage.setItem(SUPPORT_TABLE_COLUMNS_STORAGE_KEY, sessionValue);
        sessionStorage.removeItem(SUPPORT_TABLE_COLUMNS_STORAGE_KEY);
        return sessionSettings;
    } catch {
        return defaultSupportTableColumnsSettings();
    }
};

export const saveSupportTableColumnsSettings = (settings: SupportTableColumnsSettings) => {
    try {
        localStorage.setItem(SUPPORT_TABLE_COLUMNS_STORAGE_KEY, JSON.stringify(settings));
    } catch {
        return;
    }
};
